import React, { useEffect, useState } from 'react';
import { useI18n } from '@/i18n/LanguageContext';
import AdminDnsCoexistence from '@/components/AdminDnsCoexistence';
import {
  fetchDnsGuide,
  fetchDomainConfig,
  setDmarcRua,
  verifyEmailDomain,
  actionLabel,
  actionTone,
  priorityTone,
  authTone,
  authLabel,
  GODADDY_STEPS_EN,
  GODADDY_STEPS_ES,
  BRAND_DOMAIN,
  type DnsGuideResult,
  type GuideRecord,
  type DomainConfig,
  type DomainVerifyResult,
} from '@/lib/emailAuth';


/**
 * Admin → Email delivery → "GoDaddy DNS guide".
 *
 * Everything an operator needs to publish the four authentication records in
 * one sitting: exact Type / Host / Value / TTL, whether the row is an ADD or a
 * REPLACE of something already in the zone, and a copy button per field. The
 * DKIM value is the REAL 2048-bit public key issued for giggone.com by the
 * sending provider, not a placeholder.
 *
 * "Re-check DNS now" re-reads public DNS, asks the provider to re-verify the
 * domain, and (when alignment passes) promotes the From: identity to
 * support@giggone.com / noreply@giggone.com automatically.
 */

const Field: React.FC<{
  label: string;
  value: string;
  mono?: boolean;
  onCopy: () => void;
  copied: boolean;
  copyLabel: string;
  copiedLabel: string;
}> = ({ label, value, mono = true, onCopy, copied, copyLabel, copiedLabel }) => (
  <div className="flex items-start gap-2">
    <span className="mt-2 w-16 shrink-0 text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</span>
    <code
      className={`flex-1 break-all rounded-lg bg-slate-900 px-2 py-2 text-[11px] leading-relaxed text-emerald-300 ${
        mono ? 'font-mono' : ''
      }`}
    >
      {value}
    </code>
    <button
      onClick={onCopy}
      className="shrink-0 rounded-lg border border-slate-300 px-2.5 py-2 text-[11px] font-bold text-slate-700 transition hover:bg-slate-50"
    >
      {copied ? copiedLabel : copyLabel}
    </button>
  </div>
);

const AdminGoDaddyGuide: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [guide, setGuide] = useState<DnsGuideResult | null>(null);
  const [verify, setVerify] = useState<DomainVerifyResult | null>(null);
  const [config, setConfig] = useState<DomainConfig | null>(null);
  const [rua, setRua] = useState('');
  const [ruaMsg, setRuaMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState('');

  const load = async (alsoVerify: boolean) => {
    setBusy(true);
    setErr('');
    const g = await fetchDnsGuide();
    if ('error' in g) setErr(g.error);
    else {
      setGuide(g);
      setRua(g.dmarcMailbox.address);
    }
    const c = await fetchDomainConfig();
    if (c) setConfig(c);
    if (alsoVerify) {
      const v = await verifyEmailDomain(true);
      if (!('error' in v)) setVerify(v);
    }
    setBusy(false);
  };

  /**
   * Persist a new DMARC aggregate-report destination, then regenerate the guide
   * so the copy-and-paste DMARC value updates in place. No redeploy needed.
   */
  const saveRua = async () => {
    if (!config) return;
    setRuaMsg('');
    const res = await setDmarcRua(config.id, rua);
    if ('error' in res) {
      setRuaMsg(res.error);
      return;
    }
    setRuaMsg(es ? 'Guardado' : 'Saved');
    await load(false);
    setTimeout(() => setRuaMsg(''), 2000);
  };

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const copy = (value: string, id: string) => {
    navigator.clipboard?.writeText(value);
    setCopied(id);
    setTimeout(() => setCopied(''), 1600);
  };

  const liveStatus = (id: string) => verify?.tasks?.find((t) => t.id === id)?.status;
  const steps = es ? GODADDY_STEPS_ES : GODADDY_STEPS_EN;
  const copyLabel = es ? 'Copiar' : 'Copy';
  const copiedLabel = es ? 'Copiado' : 'Copied';

  const allText = guide
    ? guide.records
        .filter((r) => r.action !== 'skip')
        .map(
          (r) =>
            `#${r.step} ${r.title}\nType: ${r.type}\nName/Host: ${r.godaddyHost}\nValue: ${r.value}\nTTL: ${r.ttl}\nAction: ${actionLabel(
              r.action,
            )}${r.existing ? `\nExisting: ${r.existing}` : ''}\n`,
        )
        .join('\n')
    : '';

  return (
    <section className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-extrabold tracking-tight text-slate-900">
              {es ? 'Guía DNS de GoDaddy' : 'GoDaddy DNS guide'}
            </h2>
            <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-bold text-white">
              {es ? 'COPIAR Y PEGAR' : 'COPY & PASTE'}
            </span>
            {guide && guide.outstanding > 0 && (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-700 ring-1 ring-red-200">
                {guide.outstanding} {es ? 'pendientes' : 'outstanding'}
              </span>
            )}
          </div>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            {es
              ? `Valores reales para ${BRAND_DOMAIN}, leídos del proveedor de envío y comparados con el DNS publicado ahora mismo. La clave DKIM de abajo es la clave pública real de 2048 bits, no un ejemplo.`
              : `Real values for ${BRAND_DOMAIN}, read from the sending provider and compared against what is published in DNS right now. The DKIM value below is the real 2048-bit public key, not a placeholder.`}
          </p>
        </div>
        <div className="flex gap-2">
          {allText && (
            <button
              onClick={() => copy(allText, 'all')}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
            >
              {copied === 'all' ? copiedLabel : es ? 'Copiar todo' : 'Copy all'}
            </button>
          )}
          <button
            onClick={() => load(true)}
            disabled={busy}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? (es ? 'Verificando…' : 'Checking…') : es ? 'Volver a verificar DNS' : 'Re-check DNS now'}
          </button>
        </div>
      </div>

      {err && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm font-medium text-red-700 ring-1 ring-red-200">{err}</p>}

      {guide && (
        <>
          {/* Provider connection state */}
          <div
            className={`mt-4 rounded-xl p-3 text-sm ring-1 ${
              guide.provider.hasLiveDkimKey ? 'bg-emerald-50 ring-emerald-200' : 'bg-amber-50 ring-amber-200'
            }`}
          >
            <p className="font-bold text-slate-900">
              {guide.provider.hasLiveDkimKey
                ? es
                  ? `Dominio de envío creado en el proveedor · clave DKIM real emitida · estado: ${guide.provider.state}`
                  : `Sending domain created at the provider · real DKIM key issued · state: ${guide.provider.state}`
                : es
                  ? 'El proveedor aún no devolvió una clave DKIM real'
                  : 'The provider has not returned a live DKIM key yet'}
            </p>
            <p className="mt-1 text-xs text-slate-600">
              {es
                ? 'El estado pasa a "active" solo después de publicar los registros. Esta pantalla vuelve a pedir la verificación cada vez que pulsas "Volver a verificar DNS".'
                : 'The state only flips to "active" after the records are published. This panel asks the provider to re-verify every time you press "Re-check DNS now".'}
              {guide.provider.error ? ` · ${guide.provider.error}` : ''}
            </p>
          </div>

          {/* Click path */}
          <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-slate-500">
            {es ? 'Dónde pegarlo en GoDaddy' : 'Where to paste this in GoDaddy'}
          </h3>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-600">
            {steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>

          {/* Records */}
          <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-slate-500">
            {es ? 'Registros exactos' : 'The exact records'}
          </h3>
          <div className="mt-2 space-y-4">
            {guide.records.map((r: GuideRecord) => (
              <div
                key={r.id}
                className={`rounded-xl border p-3 ${
                  r.action === 'skip' ? 'border-slate-200 bg-slate-50 opacity-90' : 'border-slate-200'
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-white">
                    {r.step}
                  </span>
                  <span className="text-sm font-bold text-slate-900">{r.title}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ring-1 ${priorityTone(r.priority)}`}>
                    {r.priority}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ring-1 ${actionTone(r.action)}`}>
                    {actionLabel(r.action, es)}
                  </span>
                  {liveStatus(r.id) && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${authTone(liveStatus(r.id))}`}
                    >
                      {es ? 'EN VIVO' : 'LIVE'} {authLabel(liveStatus(r.id))}
                    </span>
                  )}
                </div>

                <div className="mt-3 space-y-2">
                  <div className="flex flex-wrap gap-3 text-xs">
                    <span className="rounded bg-slate-100 px-2 py-1 font-bold text-slate-700">
                      {es ? 'Tipo' : 'Type'}: {r.type}
                    </span>
                    <span className="rounded bg-slate-100 px-2 py-1 font-bold text-slate-700">TTL: {r.ttl}</span>
                    <span className="rounded bg-slate-100 px-2 py-1 font-mono text-slate-600">{r.fqdn}</span>
                  </div>
                  <Field
                    label={es ? 'Host' : 'Host'}
                    value={r.godaddyHost}
                    onCopy={() => copy(r.godaddyHost, `${r.id}-host`)}
                    copied={copied === `${r.id}-host`}
                    copyLabel={copyLabel}
                    copiedLabel={copiedLabel}
                  />
                  <Field
                    label={es ? 'Valor' : 'Value'}
                    value={r.value}
                    onCopy={() => copy(r.value, `${r.id}-value`)}
                    copied={copied === `${r.id}-value`}
                    copyLabel={copyLabel}
                    copiedLabel={copiedLabel}
                  />
                </div>

                {r.existing && (
                  <p className="mt-2 rounded-lg bg-slate-100 p-2 text-[11px] text-slate-600">
                    <span className="font-bold">{es ? 'Valor actual: ' : 'Currently published: '}</span>
                    <code className="break-all font-mono">{r.existing}</code>
                  </p>
                )}
                <p className="mt-2 text-xs text-slate-600">{r.note}</p>
                {r.verify !== '—' && (
                  <p className="mt-1 font-mono text-[11px] text-slate-400">{r.verify}</p>
                )}
              </div>
            ))}
          </div>

          {/* DMARC report destination — a rua pointing at a mailbox that does not
              exist silently discards every aggregate report, so it is editable. */}
          <div
            className={`mt-6 rounded-xl p-4 ring-1 ${
              guide.dmarcMailbox.deliverable ? 'bg-emerald-50 ring-emerald-200' : 'bg-red-50 ring-red-200'
            }`}
          >
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-700">
              {es ? 'Buzón de informes DMARC' : 'DMARC report mailbox'}
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-slate-700">{guide.dmarcMailbox.note}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                value={rua}
                onChange={(e) => setRua(e.target.value)}
                placeholder="info@giggone.com"
                className="min-w-[220px] flex-1 rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs text-slate-800 focus:border-slate-900 focus:outline-none"
              />
              <button
                onClick={saveRua}
                disabled={busy || !config || rua.trim().toLowerCase() === guide.dmarcMailbox.address}
                className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white transition hover:bg-slate-800 disabled:opacity-40"
              >
                {ruaMsg || (es ? 'Guardar y regenerar' : 'Save & regenerate')}
              </button>
            </div>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-[11px] text-slate-600">
              {guide.dmarcMailbox.alternatives.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </div>

          {/* What must NOT be touched + the sending-domain decisions */}
          <AdminDnsCoexistence coexistence={guide.coexistence} decisions={guide.decisions} es={es} />


          {/* Published snapshot */}
          <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-slate-500">
            {es ? 'Instantánea del DNS actual' : 'Snapshot of the zone right now'}
          </h3>
          <div className="mt-2 grid gap-2 text-[11px] sm:grid-cols-2">
            {[
              { k: 'SPF (root)', v: guide.published.spf },
              { k: 'DMARC', v: guide.published.dmarc },
              { k: `DKIM (${guide.records[1]?.godaddyHost})`, v: guide.published.dkim },
              { k: es ? 'MX raíz (Microsoft 365 — no tocar)' : 'Root MX (Microsoft 365 — do not touch)', v: guide.published.rootMx.join(', ') },
              { k: es ? 'CNAME mg (no requerido)' : 'mg CNAME (not required)', v: guide.published.mgCname },
              { k: es ? 'CNAME email (de GoDaddy — no tocar)' : 'email CNAME (GoDaddy\u2019s — do not touch)', v: guide.published.emailCname },
              { k: es ? 'Registros SPF' : 'SPF record count', v: String(guide.published.spfRecordCount) },
              { k: es ? 'Dominio de envío' : 'Sending domain', v: guide.sendingDomain },
            ].map((row) => (

              <div key={row.k} className="rounded-lg bg-slate-50 p-2">
                <span className="font-bold text-slate-700">{row.k}: </span>
                <code className="break-all font-mono text-slate-600">
                  {row.v || (es ? 'no publicado' : 'not published')}
                </code>
              </div>
            ))}
          </div>

          <p className="mt-4 rounded-xl bg-blue-50 p-3 text-xs font-medium text-blue-900 ring-1 ring-blue-200">
            {guide.propagation}
            <br />
            <span className="font-bold">
              {es
                ? 'No hace falta que te quedes esperando: una tarea automática revisa el DNS cada hora, pide la verificación al proveedor y cambia el remitente a support@giggone.com / noreply@giggone.com en cuanto SPF + DKIM + DMARC pasen.'
                : 'You do not need to sit here waiting: an hourly job re-checks DNS, asks the provider to verify the domain, and switches the sender to support@giggone.com / noreply@giggone.com the moment SPF + DKIM + DMARC pass.'}
            </span>
          </p>


          {verify && (
            <p className="mt-3 text-[11px] text-slate-500">
              {es ? 'Enviando ahora como' : 'Currently sending as'}{' '}
              <span className="font-mono font-bold">@{verify.activeFromDomain}</span> ·{' '}
              {es ? 'Gmail' : 'Gmail'} {verify.providerScores.gmail} · Outlook {verify.providerScores.outlook} · Yahoo{' '}
              {verify.providerScores.yahoo} · Apple {verify.providerScores.apple}
              {verify.promoted && (es ? ' · remitente promovido a @giggone.com' : ' · From: promoted to @giggone.com')}
            </p>
          )}
          <p className="mt-1 text-[11px] text-slate-400">
            {es ? 'Última lectura' : 'Last read'}: {new Date(guide.checkedAt).toLocaleString()}
          </p>
        </>
      )}
    </section>
  );
};

export default AdminGoDaddyGuide;
